import { useEffect, useState } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

let stripePromise: Promise<Stripe> | undefined;

export const useStripe = () => {
  const [stripe, setStripe] = useState<Stripe>();

  useEffect(() => {
    if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
      return;
    }

    if (!stripePromise) {
      stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string).then(
        (stripe) => {
          if (!stripe) {
            throw new Error('Stripe failed to load');
          }

          return stripe;
        }
      );
    }

    stripePromise.then(setStripe);
  }, []);

  return stripe;
};

export const useStripeAccount = ({ companyId }: { companyId: string | null }) => {
  const fetch = useFetch<Endpoints['GET /providers/stripe/company/:id']>();

  return useQuery({
    queryKey: ['stripeAccount', companyId],
    queryFn: () => fetch(`/providers/stripe/company/${companyId}`),
    enabled: !!companyId,
  });
};

export const useGetOnboardingLink = () => {
  const fetch = useFetch<Endpoints['POST /providers/stripe/company/:id/onboarding-link']>();

  return useMutation({
    mutationFn: ({ companyId, accountTokenId }: { companyId: string; accountTokenId?: string }) =>
      fetch(`/providers/stripe/company/${companyId}/onboarding-link`, undefined, {
        method: 'POST',
        body: { accountTokenId },
      }),
  });
};

export const useSetupDebitPaymentLink = () => {
  const fetch =
    useFetch<Endpoints['POST /providers/stripe/company/:id/setup-debit-payment-link']>();

  return useMutation({
    mutationFn: ({ companyId }: { companyId: string }) =>
      fetch(`/providers/stripe/company/${companyId}/setup-debit-payment-link`, undefined, {
        method: 'POST',
      }),
  });
};
